import _ from "lodash";
/** external */
import React, { Fragment, useEffect, useRef } from "react";
import { connect } from "react-redux";
import classNames from "classnames";
import { IconButton, EmptyState, Button, Tooltip } from "@toddle-edu/ds-web";
import { CloseOutlined, TickCircleFilled } from "@toddle-edu/ds-icons";
import { LEIllustraton } from "@toddle-edu/ds-theme";
import PropTypes from "prop-types";
/** internal */
import { graphql, compose } from "lib/apolloClient";
import { useI18n } from "Hooks";
import withLoader from "UIComponents/GqlWithLoader";
import {
  getFormattedQuestion,
  getLibraryQuestionCardDetails,
  getLibraryQuestionCardDetailsV2,
  isMutationAndQueriesV2Enabled,
} from "Worksheet/modules/WorksheetUtils";
import {
  isWorksheetQueriesV2Enabled,
  getIsToddleLibraryWorksheetQueriesV2Enabled,
} from "Worksheet/modules/WorksheetFeatureFlags";
import {
  MODE_ASSESSMENT_VIEW,
  QUESTION_TYPE_DETAILS,
  QUESTION_TAG_SUBJECT,
  QUESTION_TAG_TOPIC,
  TODDLE_LIBRARY,
  SCHOOL_LIBRARY,
  QUESTION_IMPORT_LIMIT,
  MODE_TEACHER_PREVIEW,
} from "Worksheet/modules/WorksheetConstants";
import {
  getQuestionLibraryQuestionQuery,
  getQuestionLibraryQuestionQueryV2,
} from "Worksheet/modules/WorksheetQueries";
import { getQuestionLibraryQuestionFromCache } from "Worksheet/modules/WorksheetGraphqlHelpers";
import {
  getDifficultyLevelOptions,
  getLibraryTagDetails,
  getDummySubmissionForLibrary,
  getDummySubmissionForLibraryV2,
} from "QuestionBank/modules/Utils";
import { CACHE_AND_NETWORK } from "Constants/stringConstants";
import Question from "Worksheet/components/Question";
import ConditionalWrapper from "Utils/ConditionalWrapper";
/** relative */
import classes from "./QuestionSidebarPreview.scss";

const QuestionSidebarPreview = props => {
  const {
    questionData,
    previewQuestionId,
    isFromInternalTool,
    toggleQuestionPreviewModal,
    addQuestionToSelection,
    selectedQuestions,
    libraryType,
    disableSelection,
    isLibraryStudentPreview,
    isToddleLibrary,
    isQueriesV2Enabled,
  } = props;

  const scrollRef = useRef(null);

  const resetScroll = () => {
    if (scrollRef && scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  };
  useEffect(() => {
    resetScroll();
  }, [previewQuestionId]);

  const { t } = useI18n(["common", "worksheet"]);

  const isSideBarPreview = true;
  const isSchoolLibrary = _.isEqual(libraryType, SCHOOL_LIBRARY);

  const libraryCardDetails = isQueriesV2Enabled
    ? getLibraryQuestionCardDetailsV2({
        question: questionData,
        t,
        isSideBarPreview,
        isToddleLibrary,
      })
    : //WORKSHEET_RESTRUCTURING:Remove getLibraryQuestionCardDetails once support is added in internal tool and toddle library
      getLibraryQuestionCardDetails({
        questionResource: questionData,
        t,
        isSideBarPreview,
        isToddleLibrary,
      });

  const tagLabels = isFromInternalTool
    ? getLibraryTagDetails(questionData, t, isSideBarPreview)
    : _.get(libraryCardDetails, "tagLabels");

  //For sub-questions, we need to extract the children from the question data and wrap them in an object with "item" key
  const subQuestions = isQueriesV2Enabled
    ? _.map(_.get(questionData, "children.edges", []), edge => ({ item: edge }))
    : _.map(_.get(questionData, "childrenV2.edges", []), "node");
  let submissions = {};
  /**
   * This code generates sample student submissions when in library preview mode.
   * This allows user to preview how students would see and interact with the question without needing real student submissions.
   */
  if (isLibraryStudentPreview) {
    submissions = isQueriesV2Enabled
      ? getDummySubmissionForLibraryV2(questionData)
      : getDummySubmissionForLibrary(questionData);
  }

  const mode = isLibraryStudentPreview
    ? MODE_TEACHER_PREVIEW
    : MODE_ASSESSMENT_VIEW;

  //for school library,we need to wrap the question data in an object with "item" key
  const question = isQueriesV2Enabled ? { item: questionData } : questionData;
  const formattedQuestion = getFormattedQuestion({
    question,
    mode,
    submissions,
    isEvaluationApplicable: false,
    isSharedWithStudent: false,
    isStudentEvaluationEnabled: false,
    isStudentSubmissionEnabled: false,
    isEvaluated: false,
    isSubmitted: false,
    isWorksheetEvaluated: false,
    subQuestions,
    questionIndex: 1,
    isFromLibrary: true,
    t,
  });

  const associatedEntities = _.get(
    formattedQuestion,
    "questionData.associatedEntities",
    []
  );
  const groupedTags = _.groupBy(associatedEntities, "__typename");
  let labels = [];
  _.forEach(groupedTags, (value, key) => {
    if (key !== "Subject" && key !== "GenericTag") {
      labels = _.concat(labels, value);
    }
  });

  const difficultyLevelOptions = getDifficultyLevelOptions(t);
  const difficultyLevel = _.find(difficultyLevelOptions, {
    value: formattedQuestion?.questionData?.difficulty,
  });

  const questionResourceId = _.get(formattedQuestion, "resourceId", "");
  const questionId = _.get(formattedQuestion, "id", "");

  const questionDetailsClass = classNames(
    { [classes.questionDetails]: true },
    { "text-body-3": true },
    { [classes.removeTopPadding]: isFromInternalTool }
  );

  const questionLabelClass = classNames(
    { [classes.questionLabel]: true },
    { "text-label-2": true }
  );

  const tagsClasses = classNames(classes.tags, "text-body-3");

  const questionTypeUid = _.get(formattedQuestion, "questionTypeUid", null);
  const questionTypeDetails = _.get(QUESTION_TYPE_DETAILS, questionTypeUid, {});
  const questionTypeLabel = _.get(questionTypeDetails, "localeLabel", null);
  const questionScore =
    _.toNumber(
      _.get(
        formattedQuestion,
        isQueriesV2Enabled
          ? "questionData.questionScoreV2"
          : "questionData.questionScore",
        0
      )
    ) || 0;

  const isSelected = _.some(selectedQuestions, [
    "id",
    isQueriesV2Enabled ? questionId : questionResourceId,
  ]);

  const onAddClick = () => {
    addQuestionToSelection({
      isSelected,
      libraryCardDetails,
    });
  };

  // Rendering the empty state UI
  const renderEmptyComponent = () => (
    <EmptyState
      illustration={LEIllustraton}
      subtitle={t("worksheet:select_question_for_preview")}
      subtitleStyle={{ lineHeight: "2.4rem", maxWidth: 280 }}
    />
  );

  const renderDetails = () => {
    const topic = _.get(tagLabels, QUESTION_TAG_TOPIC, null);
    const subject = _.get(tagLabels, QUESTION_TAG_SUBJECT, null);
    const difficulty = _.get(difficultyLevel, "label", null);

    const detailsToShow = _.compact([topic, subject, difficulty]);

    return (
      <div className={classes.detailsContainer}>
        <div className={questionLabelClass}>
          <div>
            {t(questionTypeLabel)}
            {/* tags */}
            <div className={questionDetailsClass}>
              <div className={tagsClasses}>
                {_.map(detailsToShow, (item, index) => {
                  return (
                    <Fragment key={`${questionResourceId}_${index}`}>
                      {index > 0 ? (
                        <div className={classes.dotSeparator} />
                      ) : null}
                      {item}
                    </Fragment>
                  );
                })}
              </div>
            </div>
          </div>
          <div className={classes.rightContainer}>
            {!isLibraryStudentPreview ? (
              <div className="text-body-2">
                {t("common:mark_with_number", { count: questionScore })}
              </div>
            ) : null}
            {isSchoolLibrary || isToddleLibrary ? (
              !isSelected ? (
                <ConditionalWrapper
                  type={"wrapperAsElement"}
                  condition={disableSelection}
                  wrapper={
                    <Tooltip
                      tooltip={t(
                        "worksheet:you_can_only_add_up_to_questions_at_a_time_with_count",
                        { count: QUESTION_IMPORT_LIMIT }
                      )}
                      placement="bottom"
                      overlayInnerStyle={{
                        maxWidth: "136px",
                        textAlign: "center",
                      }}
                    />
                  }
                >
                  <Button
                    dsVersion="2.0"
                    variant="progressive"
                    _legacy_variant="progressive"
                    _legacy_size={"medium"}
                    onClick={onAddClick}
                    disabled={disableSelection}
                    testId="worksheet-questionSidebarPreview-addQuestionToSelection"
                  >
                    {t("common:select")}
                  </Button>
                </ConditionalWrapper>
              ) : (
                <Button
                  dsVersion="2.0"
                  variant="neutral"
                  type="outlined"
                  _legacy_variant="outlined"
                  _legacy_size={"medium"}
                  onClick={onAddClick}
                  icon={<TickCircleFilled variant="success" />}
                  testId="worksheet-questionSidebarPreview-updateQuestionToSelection"
                >
                  {t("common:selected")}
                </Button>
              )
            ) : null}
            {isFromInternalTool ? (
              <IconButton
                onClick={toggleQuestionPreviewModal}
                icon={<CloseOutlined />}
                dsVersion="2.0"
                variant="neutral"
                type="plain"
                size="medium"
                _legacy_variant={"plain"}
                _legacy_size={"medium"}
                className={classes.closeButton}
                testId="worksheet-questionSidebarPreview-toggleQuestionPreviewModal"
              />
            ) : null}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={classes.container} ref={scrollRef}>
      {!_.isEmpty(previewQuestionId) && !_.isEmpty(questionData) ? (
        <Fragment>
          {renderDetails()}
          <Question
            key={questionResourceId}
            mode={mode}
            {...formattedQuestion}
            isFromLibrary={true}
            isFromQuestionPreview={!isLibraryStudentPreview}
            isLibraryStudentPreview={isLibraryStudentPreview}
            isFromInternalTool={isFromInternalTool}
          />
        </Fragment>
      ) : (
        renderEmptyComponent()
      )}
    </div>
  );
};

QuestionSidebarPreview.propTypes = {
  isFromInternalTool: PropTypes.bool,
  toggleQuestionPreviewModal: PropTypes.func,
  previewQuestionId: PropTypes.string,
  libraryType: PropTypes.string,
  questionData: PropTypes.object,
  addQuestionToSelection: PropTypes.func,
  selectedQuestions: PropTypes.array,
};

QuestionSidebarPreview.defaultProps = {
  isFromInternalTool: false,
  libraryType: "",
  isQueriesV2Enabled: false,
};

const mapStateToProps = (state, ownProps) => {
  const { isFromInternalTool, libraryType } = ownProps;
  const isToddleLibrary = _.isEqual(libraryType, TODDLE_LIBRARY);

  // For internal tool use mutation flag; for Toddle Library use dedicated flag; for School Library use worksheet V2 flag
  const isQueriesV2Enabled = isFromInternalTool
    ? isMutationAndQueriesV2Enabled()
    : isToddleLibrary
    ? getIsToddleLibraryWorksheetQueriesV2Enabled()
    : isWorksheetQueriesV2Enabled(isToddleLibrary, isFromInternalTool);
  return {
    isData: true,
    isLoading: false,
    isToddleLibrary,
    isQueriesV2Enabled,
  };
};

const mapActionCreators = {};

export default compose(
  connect(mapStateToProps, mapActionCreators),
  graphql(getQuestionLibraryQuestionQuery, {
    name: "getQuestionLibraryQuestion",
    skip: ({ previewQuestionId, libraryType, isQueriesV2Enabled }) => {
      return (
        _.isEmpty(previewQuestionId) ||
        !_.includes([SCHOOL_LIBRARY, TODDLE_LIBRARY], libraryType) ||
        isQueriesV2Enabled
      );
    },
    options: ({ previewQuestionId, libraryType }) => ({
      fetchPolicy: CACHE_AND_NETWORK,
      variables: {
        id: previewQuestionId,
        portalType: _.isEqual(libraryType, TODDLE_LIBRARY)
          ? "COMMUNITY"
          : "PLANNER",
      },
    }),
    props({
      getQuestionLibraryQuestion,
      ownProps: { previewQuestionId, isData, isLoading },
    }) {
      const { networkStatus } = getQuestionLibraryQuestion;

      const cacheData = getQuestionLibraryQuestionFromCache({
        id: previewQuestionId,
      });

      return {
        questionData: cacheData,
        isData: !_.isEmpty(cacheData) && isData,
        isLoading: _.includes([1, 2], networkStatus) || isLoading,
      };
    },
  }),
  graphql(getQuestionLibraryQuestionQueryV2, {
    name: "getQuestionLibraryQuestionV2",
    skip: ({ previewQuestionId, libraryType, isQueriesV2Enabled }) => {
      return (
        _.isNil(previewQuestionId) ||
        _.isEmpty(previewQuestionId) ||
        !_.includes([SCHOOL_LIBRARY, TODDLE_LIBRARY], libraryType) ||
        !isQueriesV2Enabled
      );
    },
    options: ({ previewQuestionId, libraryType }) => ({
      fetchPolicy: CACHE_AND_NETWORK,
      variables: {
        id: previewQuestionId,
        portalType: _.isEqual(libraryType, TODDLE_LIBRARY)
          ? "COMMUNITY"
          : "PLANNER",
        isSchoolLibrary: _.isEqual(libraryType, SCHOOL_LIBRARY),
      },
    }),
    props({ getQuestionLibraryQuestionV2, ownProps: { isData, isLoading } }) {
      const { networkStatus } = getQuestionLibraryQuestionV2;

      const cacheData = _.get(getQuestionLibraryQuestionV2, "node", {});

      return {
        questionData: cacheData,
        isData: !_.isEmpty(cacheData) && isData,
        isLoading: _.includes([1, 2], networkStatus) || isLoading,
      };
    },
  }),
  withLoader
)(QuestionSidebarPreview);
